import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Users, ArrowRight, Loader2, MessageSquare, GraduationCap, Sparkles } from "lucide-react";

const JoinCommunity = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      setError("Please provide your name, email, and a password of at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), password: form.password }),
      });
      let data = {};
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data?.detail || `Sign up failed (HTTP ${res.status})`);
      setUser(data);
      toast.success(`Welcome, ${data.name}! You're part of the community.`);
      navigate("/community");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface-app" data-testid="join-community-page">
      <Header />
      <main className="flex-1">
        <section className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 py-14 relative">
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3">Community</p>
            <h1 className="text-3xl sm:text-5xl font-heading font-bold leading-tight">
              Connect with the Aspiritech community
            </h1>
            <p className="text-slate-300 mt-4 max-w-2xl">
              Join the community feed, our alumni network, and the success-story circle. Meet people who
              get it — no Academy application required.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_320px] gap-8">
          <Card className="border border-slate-200">
            <CardContent className="p-6 sm:p-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm mb-4" data-testid="community-signup-error">
                  {error}
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="cm-name">Full name *</Label>
                  <Input id="cm-name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="cm-name" />
                </div>
                <div>
                  <Label htmlFor="cm-email">Email *</Label>
                  <Input id="cm-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="cm-email" />
                </div>
                <div>
                  <Label htmlFor="cm-password">Choose a password *</Label>
                  <Input id="cm-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} minLength={6} required data-testid="cm-password" />
                </div>
                <Button type="submit" disabled={loading} className="btn-primary w-full h-11" data-testid="cm-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Join the community <ArrowRight className="w-4 h-4 ml-1" /></>)}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Already have an account?{" "}
                  <Link to="/login" className="text-[#1E3A8A] hover:underline" data-testid="cm-existing-link">Sign in instead</Link>
                </p>
              </form>
            </CardContent>
          </Card>

          <aside className="space-y-4">
            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-slate-900 font-heading font-bold">
                  <Users className="w-5 h-5 text-[#1E3A8A]" /> What you'll get
                </div>
                <ul className="text-sm text-slate-700 mt-4 space-y-2">
                  <li className="flex gap-2"><MessageSquare className="w-4 h-4 text-[#1E3A8A] mt-0.5" /> Access to the community feed</li>
                  <li className="flex gap-2"><GraduationCap className="w-4 h-4 text-[#1E3A8A] mt-0.5" /> The alumni network &amp; directory</li>
                  <li className="flex gap-2"><Sparkles className="w-4 h-4 text-[#FFD507] mt-0.5" /> Success stories &amp; peer support</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="border border-[#FFD507]/40 bg-[#FFD507]/5">
              <CardContent className="p-6">
                <p className="text-sm text-slate-700">
                  Ready for more? You can{" "}
                  <Link to="/apply" className="text-[#1E3A8A] hover:underline font-semibold">enroll in the Academy</Link>{" "}
                  or{" "}
                  <Link to="/talent-signup" className="text-[#1E3A8A] hover:underline font-semibold">join the Talent Portal</Link>{" "}
                  anytime.
                </p>
              </CardContent>
            </Card>
          </aside>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default JoinCommunity;
